/**
 * The only place that turns a `HollyPose` into transforms.
 *
 * `RosalieRig`'s two jobs, on a different animal:
 *
 * 1. **Bind capture.** Her head carries 0.38 rad of nose-down and her legs are
 *    placed at their joint heights by the factory. Poses are deltas, so the bind
 *    transforms are read once here and every applied value is added to them.
 *    Without it the first frame of any clip snaps her head level and she reads
 *    as an alpaca — which `holly.ts` records as an actual failed turnaround.
 *
 * 2. **Clamping what a clip must not be trusted with.** The breath and the rear
 *    both have hard ceilings here rather than in the clips, for the reason D-002
 *    gives about squash: a limit that lives in the caller is a limit the next
 *    caller exceeds, one "just a bit more on this one moment" at a time.
 *
 * ## The rear, which is why this class is not three lines
 *
 * `rear` rotates her whole body about her HIND HOOF CONTACT, not about her
 * origin. Rotating about the origin — which is what `rotation.x` alone does —
 * swings her back feet down through the floor by 0.31 m at the angles beat 9
 * needs, and the shot is from the front, so it is not subtle. The rig therefore
 * writes a rotation AND the translation that keeps `HOLLY_REAR_PIVOT` fixed.
 */

import * as THREE from 'three';
import { findHollyParts, HOLLY_REAR_PIVOT, measureMuzzle, type HollyParts } from '@/models/holly';
import type { HollyLegPose, HollyPose } from './hollyTypes';

/**
 * Ribcage swell ceiling. Deliberately smaller than Rosalie's 5 % squash cap.
 *
 * She is 1.24 m of animal and the camera sees her whole flank; 2 % is already a
 * 6 mm move at her widest, which is visible and is as much as breathing is. This
 * is the channel a later clip would reach for to make her heave, and heaving is
 * acting — the same line `hollyTypes.ts` draws around the attention channels.
 */
const BREATHE_LIMIT = 0.02;

/**
 * How far she may rear, radians. 0.95 is about 54°.
 *
 * A ewe putting her forefeet on a gate gets her chest to roughly this and no
 * further without leaving the ground, and past it the pose stops reading as an
 * animal reaching and starts reading as one falling over backwards. The gate in
 * `tests/e2e/holly-rig.spec.ts` measures what this actually buys her against the
 * real bar height, so if this number ever has to move, the failure will say so
 * in metres rather than in opinions.
 */
const REAR_LIMIT = 0.95;

interface BoundLeg {
  node: THREE.Object3D;
  bindY: number;
  bindRotX: number;
  bindRotZ: number;
}

function bindLeg(node: THREE.Object3D): BoundLeg {
  return { node, bindY: node.position.y, bindRotX: node.rotation.x, bindRotZ: node.rotation.z };
}

export class HollyRig {
  readonly parts: HollyParts;

  /**
   * Her whole group, held only so the muzzle can be measured from the top.
   *
   * `updateMatrixWorld` walks DOWN. Called on the muzzle mesh it refreshes
   * nothing, because everything that just moved — the rear, the neck, the head —
   * is above it, and the measurement would come back one frame stale in exactly
   * the pose the gate cares about.
   */
  readonly #root: THREE.Object3D;

  #neckBind: THREE.Euler;
  #headBind: THREE.Euler;
  #legs: { FL: BoundLeg; FR: BoundLeg; HL: BoundLeg; HR: BoundLeg };

  constructor(root: THREE.Object3D) {
    this.#root = root;
    this.parts = findHollyParts(root);
    this.#neckBind = this.parts.neck.rotation.clone();
    this.#headBind = this.parts.head.rotation.clone();
    this.#legs = {
      FL: bindLeg(this.parts.legFL),
      FR: bindLeg(this.parts.legFR),
      HL: bindLeg(this.parts.legHL),
      HR: bindLeg(this.parts.legHR),
    };
  }

  /** Write a pose onto the rig. Allocates nothing. */
  apply(pose: HollyPose): void {
    const p = this.parts;

    // **Clamp the COMPOSED angle, not just `rear`.** An independent review found
    // the first version capping `rear` and then adding `pose.pitch` to it
    // unbounded, so `{ rear: 0, pitch: -3 }` produced a rear of three radians
    // through a limit whose whole stated purpose is that a clip cannot be
    // trusted with one. The clamp test only exercised `rear`, so it stayed
    // green — the same half-a-conditional failure D-100 is about, in the gate
    // written for the limit.
    //
    // Nose-down pitch is still free: `REAR_LIMIT` bounds how far her front may
    // come UP, and tipping her nose toward the floor is a different motion with
    // no floor to go through.
    const composed = Math.max(0, pose.rear) + pose.pitch;
    const rear = Math.min(REAR_LIMIT, composed);
    // Negative about X lifts her front, because +Z is forward and a positive X
    // rotation carries +Z downward. Same convention as her head's nose-down bind.
    const angle = -rear;
    p.breath.rotation.set(angle, 0, pose.roll);

    // The translation that pins `HOLLY_REAR_PIVOT` while the body turns about it.
    // Written out rather than done with a Matrix4 because it runs every frame for
    // every companion on screen and this is two sines and a subtraction.
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const { y: py, z: pz } = HOLLY_REAR_PIVOT;
    const rotatedY = py * cos - pz * sin;
    const rotatedZ = py * sin + pz * cos;
    p.breath.position.set(0, py - rotatedY + pose.bob, pz - rotatedZ);

    const swell = Math.min(1 + BREATHE_LIMIT, Math.max(1 - BREATHE_LIMIT, pose.breathe));
    // X and Y only. A ribcage fills sideways and upward; scaling her along Z as
    // well would lengthen a 1.25 m animal on every breath, which reads as the
    // whole sheep growing rather than as her chest filling.
    p.breath.scale.set(swell, swell, 1);

    p.neck.rotation.set(
      this.#neckBind.x + pose.neckPitch,
      this.#neckBind.y + pose.neckYaw,
      this.#neckBind.z,
    );
    p.head.rotation.set(
      this.#headBind.x + pose.headPitch,
      this.#headBind.y + pose.headYaw,
      this.#headBind.z,
    );

    this.#applyLeg(this.#legs.FL, pose.legFL, -1);
    this.#applyLeg(this.#legs.FR, pose.legFR, 1);
    this.#applyLeg(this.#legs.HL, pose.legHL, -1);
    this.#applyLeg(this.#legs.HR, pose.legHR, 1);
  }

  /**
   * Her muzzle's height in world space, right now.
   *
   * The reach gate's instrument, and the reason `muzzle` is in `HollyParts` at
   * all. `MUZZLE_Y` was wrong by 95 mm for two sessions because the only thing
   * checking it was another constant. This measures the GEOMETRY — see
   * `measureMuzzle`, which is where the second version of that same mistake is
   * recorded.
   */
  muzzleWorldY(): number {
    return measureMuzzle(this.#root).y;
  }

  /** Her muzzle's world position. The reach gate needs Z as well as Y. */
  muzzleWorld(): THREE.Vector3 {
    return measureMuzzle(this.#root);
  }

  #applyLeg(leg: BoundLeg, pose: HollyLegPose, side: 1 | -1): void {
    leg.node.position.y = leg.bindY + pose.lift;
    leg.node.rotation.x = leg.bindRotX + pose.swing;
    leg.node.rotation.z = leg.bindRotZ + pose.splay * side;
  }
}
